import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  setupFee?: string;
  usage?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Media Management',
    price: '$200',
    description: 'Professional social media management for health clinics',
    features: [
      'Content creation and scheduling',
      'Platform management',
      'Engagement monitoring',
      'Monthly performance reports',
      'Brand consistency'
    ]
  },
  {
    name: 'Ads Management',
    price: '$350',
    description: 'Strategic ad campaigns to attract new clients',
    features: [
      'Campaign strategy and setup',
      'Ad creative development',
      'Targeting and optimization',
      'Performance tracking',
      'Monthly ROI reports',
      '+ ad spend budget'
    ]
  },
  {
    name: 'Lead Rescue System',
    price: '$150',
    setupFee: '$750',
    usage: true,
    description: 'Never miss a client again with automated follow-up',
    features: [
      'Missed call text-back',
      'Website inquiry SMS reply',
      'Simple lead log',
      'Automated follow-up',
      '24-hour implementation',
      '+ usage fees'
    ],
    highlighted: true
  },
  {
    name: 'Websites',
    price: 'From $400',
    description: 'Professional websites that convert visitors to clients',
    features: [
      'Landing pages from $400',
      'Full websites from $1,000',
      'Mobile-responsive design',
      'SEO optimization',
      'Fast loading speeds',
      'Easy content updates'
    ]
  },
  {
    name: 'Chatbots',
    price: '$250',
    setupFee: '$1,250',
    description: '24/7 AI assistant for your website',
    features: [
      'AI-powered conversations',
      'Appointment booking',
      'FAQ handling',
      'Lead capture',
      'Custom training',
      'Analytics dashboard'
    ]
  },
  {
    name: 'Full AI + Automation Package',
    price: '$700',
    setupFee: '$3,000',
    usage: true,
    description: 'Complete AI-powered business automation',
    features: [
      'Everything included',
      'Lead rescue system',
      'Website chatbot',
      'Phone agent',
      'Social media automation',
      'Priority support',
      '+ usage fees'
    ],
    highlighted: true
  }
];

export function PricingPage() {
  return (
    <>
      <Helmet>
        <title>Pricing - Priority AI Services</title>
        <meta
          name="description"
          content="Simple, transparent pricing for health clinics. From media management to complete AI automation packages."
        />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Choose the services that fit your clinic's needs. No hidden fees, no long-term contracts.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`relative rounded-lg p-8 border-2 transition-all hover:scale-105 ${
                  tier.highlighted
                    ? 'border-green-500 bg-green-500/5'
                    : 'border-blue-500/30 bg-black/40'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Zap className="w-4 h-4" />
                      POPULAR
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {tier.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {tier.description}
                  </p>

                  {tier.setupFee && (
                    <div className="text-gray-400 mb-2">
                      <span className="text-white font-bold text-lg">
                        {tier.setupFee}
                      </span>
                      <span className="text-sm"> setup</span>
                    </div>
                  )}

                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      {tier.price}
                    </span>
                    {!tier.price.includes('From') && (
                      <span className="text-gray-400">/month</span>
                    )}
                  </div>

                  {tier.usage && (
                    <p className="text-sm text-gray-500 mt-1">
                      Plus usage fees based on activity
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/#start-here"
                  className={`block w-full py-3 px-6 rounded-lg font-bold text-center transition-all ${
                    tier.highlighted
                      ? 'bg-green-500 text-black hover:bg-green-400'
                      : 'bg-blue-500 text-white hover:bg-blue-400'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-black/40 border-2 border-blue-500/30 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  What are usage fees?
                </h3>
                <p className="text-gray-400">
                  Usage fees apply to services like text messages, phone calls, and API usage.
                  For example, the Lead Rescue System sends SMS messages to your clients.
                  You only pay for what you actually use.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Are there any long-term contracts?
                </h3>
                <p className="text-gray-400">
                  No. All services are month-to-month. You can cancel anytime with 30 days notice.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Can I combine multiple services?
                </h3>
                <p className="text-gray-400">
                  Absolutely! Many clinics benefit from combining services. The Full AI + Automation
                  Package includes everything at a discounted rate.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  What's included in the setup fee?
                </h3>
                <p className="text-gray-400">
                  Setup fees cover custom configuration, training, testing, and initial optimization
                  for your specific clinic. Most setups are completed within 24-48 hours.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Do you offer support?
                </h3>
                <p className="text-gray-400">
                  Yes! All services include email support. The Full AI + Automation Package includes
                  priority support with faster response times.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Book a free 30-minute consultation to discuss which services are right for your clinic.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#start-here"
                className="px-8 py-4 bg-green-500 text-black font-bold rounded-lg hover:bg-green-400 transition-all"
              >
                Start Free Consultation
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent border-2 border-blue-500 text-blue-500 font-bold rounded-lg hover:bg-blue-500/10 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
